import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import axios from 'axios';
import { getAccessToken } from './utils/authStorage';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8020';
const isBackendApiRequest = url => typeof url === 'string' && url.startsWith(`${backendUrl}/api`);
const unsafeMethod = method => !['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase());

axios.interceptors.request.use(config => {
  if (!isBackendApiRequest(config.url)) return config;
  config.headers = config.headers || {};
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// API requests use the same stored bearer session whether they are sent by
// axios or native fetch. Responses are buffered once to prevent consumers
// from seeing an already-consumed response body.
{
  const nativeFetch = window.fetch.bind(window);
  window.__nativeFetch = nativeFetch;
  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === 'string' ? input : input?.url;
    if (!isBackendApiRequest(requestUrl)) return nativeFetch(input, init);

    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await nativeFetch(input, { ...init, headers, credentials: 'omit' });

    let body;
    try { body = await response.arrayBuffer(); } catch { body = new ArrayBuffer(0); }
    const responseHeaders = new Headers();
    response.headers.forEach((value, key) => {
      if (!['content-encoding', 'content-length'].includes(key.toLowerCase())) {
        responseHeaders.set(key, value);
      }
    });
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
    });
  };
}

// Initialize dark mode from localStorage before React renders
// This prevents flash of light mode and ensures correct initial styling
if (localStorage.getItem('darkMode') === 'true') {
  document.body.classList.add('dark-mode');
}

// Initialize RTL direction for Arabic language
const savedLanguage = localStorage.getItem('language') || 'en';
if (savedLanguage === 'ar') {
  document.documentElement.setAttribute('dir', 'rtl');
  document.documentElement.setAttribute('lang', 'ar');
} else {
  document.documentElement.setAttribute('dir', 'ltr');
  document.documentElement.setAttribute('lang', savedLanguage);
}

const root = ReactDOM.createRoot(document.getElementById("root"), {
  // The boundary reports a safe diagnostic. React 19's default caught-error
  // logger would otherwise emit the original Error/component stack again.
  onCaughtError: () => {},
});
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
